import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SMIIFY_API } from '../config/api';
import { installFetchGuard, setAccessSnapshot, setOnServerDenied } from '../utils/accessGuard';

// Stop requests the user has no access to from leaving the browser (no 403s in the console)
installFetchGuard();

// =============================================================================
// Auth + live access.
// hasPermission() uses the access the server calculated for the active branch
// (the user's own policy limited by the HQ branch limit). Until that arrives it
// falls back to the policies saved at login, so screens never flash "no access".
// =============================================================================
const AuthContext = createContext();
const ACCESS_KEY = 'smiify_access';
const REFRESH_AFTER_MS = 15 * 1000;      // on focus / page change, if older than this
const POLL_MS = 60 * 1000;               // while the tab is visible

const readJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(() => readJson(ACCESS_KEY));
  const [loading, setLoading] = useState(true);
  const lastStore = useRef(null);
  const inFlight = useRef(null);

  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('smiify_user');
      const token = localStorage.getItem('smiify_token');
      if (savedUser && token) setUser(JSON.parse(savedUser));
    } catch (error) {
      localStorage.removeItem('smiify_user');
      localStorage.removeItem('smiify_token');
      localStorage.removeItem(ACCESS_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('smiify_token');
    localStorage.removeItem('smiify_user');
    localStorage.removeItem('smiify_active_store_id');
    localStorage.removeItem(ACCESS_KEY);
    setUser(null);
    setAccess(null);
    window.location.href = '/login';
  }, []);

  /**
   * Load my access for a branch.
   * - Never asks without a branch (that would skip the HQ branch limit and flash everything).
   * - If another branch is already loading, the new one is loaded right after it.
   * - An answer for a branch that is no longer selected is ignored.
   */
  const inFlightStore = useRef(null);
  const refreshAccess = useCallback(async (storeId = lastStore.current) => {
    const token = localStorage.getItem('smiify_token');
    if (!token) return null;
    if (readJson('smiify_user')?.role === 'SUPERADMIN') return null;   // owner is never limited
    const sid = storeId && storeId !== 'global_hq' ? String(storeId) : null;
    if (!sid) return null;
    lastStore.current = sid;
    if (inFlight.current) {
      if (inFlightStore.current === sid) return inFlight.current;
      return inFlight.current.then(() => refreshAccess(sid));
    }
    inFlightStore.current = sid;
    inFlight.current = (async () => {
      try {
        const res = await fetch(`${SMIIFY_API}/access/me?storeId=${encodeURIComponent(sid)}`, {
          headers: { Authorization: `Bearer ${token}`, 'x-store-id': sid },
        });
        if (res.status === 401) { logout(); return null; }
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) return null;
        if (lastStore.current !== sid) return null;                      // branch changed meanwhile
        const next = { ...json.data, storeId: sid, loadedAt: Date.now() };
        setAccess(next);
        localStorage.setItem(ACCESS_KEY, JSON.stringify(next));
        // role changed by HQ / a manager: update the saved user so no logout is needed
        setUser((u) => {
          if (!u || !next.role || u.role === next.role) return u;
          const updated = { ...u, role: next.role };
          localStorage.setItem('smiify_user', JSON.stringify(updated));
          return updated;
        });
        return next;
      } catch (e) {
        return null; // offline: keep the last known access
      } finally {
        inFlight.current = null;
        inFlightStore.current = null;
      }
    })();
    return inFlight.current;
  }, [logout]);

  // keep access fresh: when the tab comes back, and every minute while it is open,
  // so a change made by HQ / a manager shows up without a reload
  const loadedAtRef = useRef(access?.loadedAt || 0);
  loadedAtRef.current = access?.loadedAt || 0;
  const refreshIfOld = useCallback((maxAge = REFRESH_AFTER_MS) => {
    if (Date.now() - loadedAtRef.current > maxAge) refreshAccess();
  }, [refreshAccess]);

  // The server refused something the screen thought was allowed: reload access now (menu updates at once)
  useEffect(() => {
    setOnServerDenied(user ? () => refreshIfOld(3000) : null);
    return () => setOnServerDenied(null);
  }, [user, refreshIfOld]);

  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => refreshIfOld();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshIfOld(); };
    const id = setInterval(() => { if (document.visibilityState === 'visible') refreshIfOld(POLL_MS - 1000); }, POLL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, refreshIfOld]);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${SMIIFY_API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        localStorage.setItem('smiify_token', data.token);
        localStorage.setItem('smiify_user', JSON.stringify(data.user));
        // The login answer already carries this user's live access (own policy + HQ limit of the first branch):
        // use it at once, so the first screen never shows "no access" while it loads.
        const firstStore = data.user?.assignedStores?.[0];
        const fromLogin = data.user?.policies && Object.keys(data.user.policies).length
          ? { role: data.user.role, userId: String(data.user.id || data.user._id || ''), storeId: firstStore ? String(firstStore._id || firstStore.id) : null, policies: data.user.policies, loadedAt: Date.now() }
          : null;
        if (fromLogin) localStorage.setItem(ACCESS_KEY, JSON.stringify(fromLogin));
        else localStorage.removeItem(ACCESS_KEY);
        setAccess(fromLogin);
        setUser(data.user);
        return { success: true };
      }
      return { success: false, message: data.message || 'Login failed.' };
    } catch (error) {
      return { success: false, message: 'Network error.' };
    }
  };

  /** Policies to check: live access for this user, else the ones saved at login */
  const currentPolicies = () => {
    const sameUser = !access?.userId || String(access.userId) === String(user?.id || user?._id || '');
    if (access?.policies && access.role === user?.role && sameUser) return access.policies;
    return user?.policies || null;
  };

  const hasPermission = (module, action = 'view') => {
    if (!user) return false;
    if (user.role === 'SUPERADMIN') return true;

    const policies = currentPolicies();
    if (policies && policies[module] && policies[module].actions) {
      const actions = policies[module].actions;
      // master gate: nothing inside a module works if "view" is off
      // (policies saved before "view" existed for a module are not gated)
      if (action !== 'view' && actions.view === false) return false;
      if (action === 'view' && actions.view === undefined) return Object.values(actions).some((x) => x === true);
      return actions[action] === true;
    }

    // very old user objects
    if (user.permissions && typeof user.permissions === 'object' && !Array.isArray(user.permissions)) {
      if (action !== 'view' && user.permissions[module]?.view !== true) return false;
      return user.permissions[module]?.[action] === true;
    }
    if (Array.isArray(user.permissions)) return user.permissions.includes(`${action}_${module}`) || user.permissions.includes(module);
    return false;
  };

  // keep the browser-side guard in step (set during render so the first requests already use it)
  setAccessSnapshot({ role: user?.role || null, policies: user ? currentPolicies() : null });

  /** Access is known (owner, live access, or the login copy). Until then screens show "loading", not "no access". */
  const accessReady = (() => {
    if (!user) return false;
    if (user.role === 'SUPERADMIN') return true;
    const p = currentPolicies();
    return Boolean(p && Object.keys(p).length);
  })();

  /** true if any of these actions is allowed */
  const hasAnyPermission = (module, actions = []) => actions.some((a) => hasPermission(module, a));

  /** A rule value, e.g. constraint('billing', 'maxDiscountLimit') -> '20' */
  const constraint = (module, key, fallback = undefined) => {
    if (user?.role === 'SUPERADMIN') return fallback === undefined ? true : fallback;
    const value = currentPolicies()?.[module]?.constraints?.[key];
    return value === undefined ? fallback : value;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasPermission, hasAnyPermission, constraint, access, refreshAccess, refreshIfOld, accessReady }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);